/**
 * Google Analytics 4 (GA4) Analytics Service
 *
 * This service provides comprehensive tracking for user actions, page views,
 * and conversion events using Google Analytics 4.
 *
 * On native mobile apps (iOS/Android), uses Firebase Analytics SDK.
 * On web, uses gtag.js.
 */

import { Capacitor } from '@capacitor/core';
import { FirebaseAnalytics } from '@capacitor-firebase/analytics';

// Declare gtag on window for TypeScript
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Analytics configuration
let isInitialized = false;
let measurementId: string | null = null;
let debugMode = false;

// Check if running on native platform (iOS/Android)
const isNativePlatform = Capacitor.isNativePlatform();

/**
 * Initialize Google Analytics 4 with the provided Measurement ID
 * On native platforms, initializes Firebase Analytics instead
 */
export async function initializeGA(gaId: string, debug: boolean = false): Promise<void> {
  if (isInitialized && measurementId === gaId) {
    console.log('[Analytics] Already initialized with the same ID');
    return;
  }

  measurementId = gaId;
  debugMode = debug;

  // Native platforms use Firebase Analytics (no GA measurement ID needed for native)
  if (isNativePlatform) {
    try {
      // Enable analytics collection
      await FirebaseAnalytics.setEnabled({ enabled: true });
      isInitialized = true;
      console.log('[Analytics] Firebase Analytics initialized for native platform');
      return;
    } catch (error) {
      console.error('[Analytics] Failed to initialize Firebase Analytics:', error);
      return;
    }
  }

  // Web platform uses gtag.js
  if (!gaId || !gaId.match(/^G-[A-Z0-9]+$/)) {
    console.error('[Analytics] Invalid GA4 Measurement ID:', gaId);
    return;
  }

  // Remove existing GA scripts if reinitializing
  const existingScript = document.querySelector('script[src*="googletagmanager.com/gtag/js"]');
  if (existingScript) {
    existingScript.remove();
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];

  // Define gtag function
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  // Set default timestamp
  window.gtag('js', new Date());

  // Configure GA4
  const configOptions: Record<string, any> = {
    send_page_view: false, // We'll handle page views manually for SPA
  };

  if (debug) {
    configOptions.debug_mode = true;
  }

  window.gtag('config', gaId, configOptions);

  // Load gtag.js script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
  document.head.appendChild(script);

  isInitialized = true;

  if (debug) {
    console.log('[Analytics] GA4 initialized with ID:', gaId, '(debug mode enabled)');
  } else {
    console.log('[Analytics] GA4 initialized with ID:', gaId);
  }
}

/**
 * Check if Analytics is initialized and ready
 */
export function isAnalyticsReady(): boolean {
  // Native platforms don't need a measurement ID (uses GoogleService-Info.plist / google-services.json)
  if (isNativePlatform) {
    return isInitialized;
  }
  return isInitialized && !!measurementId;
}

/**
 * Track a page view / screen view
 */
export async function trackPageView(path: string, title?: string): Promise<void> {
  if (!isAnalyticsReady()) {
    if (debugMode) console.log('[Analytics] Not initialized, skipping page view:', path);
    return;
  }

  if (isNativePlatform) {
    try {
      // Use screen view for native platforms
      await FirebaseAnalytics.setCurrentScreen({
        screenName: title || path,
        screenClassOverride: path,
      });
      if (debugMode) {
        console.log('[Analytics] Screen view:', { screenName: title || path, path });
      }
    } catch (error) {
      console.error('[Analytics] Failed to track screen view:', error);
    }
    return;
  }

  // Web platform
  const pageData: Record<string, any> = {
    page_path: path,
  };

  if (title) {
    pageData.page_title = title;
  }

  window.gtag('event', 'page_view', pageData);

  if (debugMode) {
    console.log('[Analytics] Page view:', pageData);
  }
}

/**
 * Track a custom event
 */
export async function trackEvent(
  eventName: string,
  params?: Record<string, any>
): Promise<void> {
  if (!isAnalyticsReady()) {
    if (debugMode) console.log('[Analytics] Not initialized, skipping event:', eventName);
    return;
  }

  if (isNativePlatform) {
    try {
      await FirebaseAnalytics.logEvent({
        name: eventName,
        params: params || {},
      });
      if (debugMode) {
        console.log('[Analytics] Native event:', eventName, params || {});
      }
    } catch (error) {
      console.error('[Analytics] Failed to log native event:', error);
    }
    return;
  }

  // Web platform
  window.gtag('event', eventName, params || {});

  if (debugMode) {
    console.log('[Analytics] Event:', eventName, params || {});
  }
}

/**
 * Set the user ID for user identification
 */
export async function setUserId(userId: string | null): Promise<void> {
  if (!isAnalyticsReady()) {
    if (debugMode) console.log('[Analytics] Not initialized, skipping user ID set');
    return;
  }

  if (isNativePlatform) {
    try {
      await FirebaseAnalytics.setUserId({ userId });
      if (debugMode) {
        console.log('[Analytics] Native user ID set:', userId ? `${userId.substring(0, 8)}...` : 'null');
      }
    } catch (error) {
      console.error('[Analytics] Failed to set native user ID:', error);
    }
    return;
  }

  // Web platform
  window.gtag('config', measurementId!, {
    user_id: userId,
  });

  if (debugMode) {
    console.log('[Analytics] User ID set:', userId ? `${userId.substring(0, 8)}...` : 'null');
  }
}

/**
 * Set user properties
 */
export async function setUserProperties(properties: Record<string, any>): Promise<void> {
  if (!isAnalyticsReady()) {
    if (debugMode) console.log('[Analytics] Not initialized, skipping user properties');
    return;
  }

  if (isNativePlatform) {
    try {
      // Set each property individually for native
      for (const [key, value] of Object.entries(properties)) {
        await FirebaseAnalytics.setUserProperty({
          key,
          value: String(value),
        });
      }
      if (debugMode) {
        console.log('[Analytics] Native user properties set:', properties);
      }
    } catch (error) {
      console.error('[Analytics] Failed to set native user properties:', error);
    }
    return;
  }

  // Web platform
  window.gtag('set', 'user_properties', properties);

  if (debugMode) {
    console.log('[Analytics] User properties set:', properties);
  }
}

// ============================================================================
// Pre-defined Event Tracking Functions
// ============================================================================

// Authentication Events
export function trackSignUp(method: string = 'email'): void {
  trackEvent('sign_up', { method });
}

export function trackLogin(method: string = 'email'): void {
  trackEvent('login', { method });
}

export function trackLogout(): void {
  trackEvent('logout');
}

// League Events
export function trackLeagueCreate(leagueId: number, leagueName: string, leagueType: string): void {
  trackEvent('league_create', {
    league_id: leagueId,
    league_name: leagueName,
    league_type: leagueType,
  });
}

export function trackLeagueJoin(leagueId: number, leagueName: string, method: string = 'direct'): void {
  trackEvent('league_join', {
    league_id: leagueId,
    league_name: leagueName,
    join_method: method, // 'direct', 'invite', 'discover'
  });
}

export function trackLeagueLeave(leagueId: number, leagueName: string): void {
  trackEvent('league_leave', {
    league_id: leagueId,
    league_name: leagueName,
  });
}

export function trackLeagueView(leagueId: number, leagueName: string, leagueType: string): void {
  trackEvent('league_view', {
    league_id: leagueId,
    league_name: leagueName,
    league_type: leagueType,
  });
}

// Submission Events
export function trackSubmissionCreate(
  leagueId: number,
  roundId: number,
  submissionType: string,
  isFirstSubmission: boolean = false
): void {
  trackEvent('submission_create', {
    league_id: leagueId,
    round_id: roundId,
    submission_type: submissionType, // 'music', 'video'
    is_first: isFirstSubmission,
  });

  // Track as conversion if it's user's first submission
  if (isFirstSubmission) {
    trackEvent('first_submission', {
      league_id: leagueId,
    });
  }
}

export function trackSubmissionView(submissionId: number, submissionType: string): void {
  trackEvent('submission_view', {
    submission_id: submissionId,
    submission_type: submissionType,
  });
}

// Voting Events
export function trackVoteCast(
  roundId: number,
  leagueId: number,
  pointsAllocated: number,
  isFirstVote: boolean = false
): void {
  trackEvent('vote_cast', {
    round_id: roundId,
    league_id: leagueId,
    points: pointsAllocated,
    is_first: isFirstVote,
  });

  // Track as conversion if it's user's first vote
  if (isFirstVote) {
    trackEvent('first_vote', {
      league_id: leagueId,
    });
  }
}

export function trackVoteFinalize(roundId: number, leagueId: number, totalPoints: number): void {
  trackEvent('vote_finalize', {
    round_id: roundId,
    league_id: leagueId,
    total_points: totalPoints,
  });
}

// Music Events
export function trackMusicPlay(
  service: string,
  trackTitle: string,
  artistName: string,
  context?: string
): void {
  trackEvent('music_play', {
    music_service: service, // 'spotify', 'apple_music', 'youtube', etc.
    track_title: trackTitle,
    artist_name: artistName,
    play_context: context, // 'submission', 'preview', 'playlist'
  });
}

export function trackMusicServiceConnect(service: string, success: boolean): void {
  trackEvent('music_service_connect', {
    music_service: service,
    success: success,
  });
}

export function trackPlaylistCreate(service: string, trackCount: number): void {
  trackEvent('playlist_create', {
    music_service: service,
    track_count: trackCount,
  });
}

// Engagement Events
export function trackCommentCreate(submissionId: number, isPublic: boolean): void {
  trackEvent('comment_create', {
    submission_id: submissionId,
    is_public: isPublic,
  });
}

export function trackShareClick(contentType: string, shareMethod: string): void {
  trackEvent('share_click', {
    content_type: contentType, // 'league', 'submission', 'profile'
    share_method: shareMethod, // 'copy_link', 'native_share', etc.
  });
}

export function trackInviteSend(leagueId: number, method: string): void {
  trackEvent('invite_send', {
    league_id: leagueId,
    invite_method: method, // 'link', 'email', 'social'
  });
}

// Search Events
export function trackSearch(searchQuery: string, resultCount: number): void {
  trackEvent('search', {
    search_term: searchQuery,
    result_count: resultCount,
  });
}

// Error Tracking
export function trackError(errorType: string, errorMessage: string, context?: string): void {
  trackEvent('app_error', {
    error_type: errorType,
    error_message: errorMessage.substring(0, 100), // Limit message length
    error_context: context,
  });
}

/**
 * Disable analytics tracking (for GDPR compliance)
 */
export async function disableAnalytics(): Promise<void> {
  if (isNativePlatform) {
    try {
      await FirebaseAnalytics.setEnabled({ enabled: false });
      console.log('[Analytics] Native tracking disabled for this user');
    } catch (error) {
      console.error('[Analytics] Failed to disable native analytics:', error);
    }
    return;
  }

  if (measurementId) {
    // Set GA to not track this user
    (window as any)[`ga-disable-${measurementId}`] = true;
    console.log('[Analytics] Tracking disabled for this user');
  }
}

/**
 * Enable analytics tracking
 */
export async function enableAnalytics(): Promise<void> {
  if (isNativePlatform) {
    try {
      await FirebaseAnalytics.setEnabled({ enabled: true });
      console.log('[Analytics] Native tracking enabled for this user');
    } catch (error) {
      console.error('[Analytics] Failed to enable native analytics:', error);
    }
    return;
  }

  if (measurementId) {
    (window as any)[`ga-disable-${measurementId}`] = false;
    console.log('[Analytics] Tracking enabled for this user');
  }
}
